import React, { JSX, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./css/App.css";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";

import Navigation from "./components/Navigation";
import Home from "./pages/Home";
import TemplateList from "./pages/TemplateList";
import TemplateEditor from "./pages/TemplateEditor";
import CreateDocuments from "./pages/CreateDocuments";
import { AUTH_REDIRECT_PATH, AuthError } from "./services/UserAuthService";
import DocumentList from "./pages/DocumentList";
import ErrorModal from "./components/modals/ErrorModal";
import logger from "./utils/logging";
import { Dependencies } from "./init/config";
import OAuth2Callback from "./components/auth/OAuth2Callback";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Page404 from "./pages/Page404";
import Footer from "./components/Footer";
import { applyAppMetaTags } from "./utils/meta";

interface ErrorModalState {
    show: boolean;
    error: AuthError | null;
}

function App({ dependencies }: { dependencies: Dependencies }): JSX.Element {
    const [initialized, setInitialized] = useState<boolean>(dependencies.authService.isInitialized());
    const [errorModal, setErrorModal] = useState<ErrorModalState>({ show: false, error: null });
    const { t } = useTranslation(["auth", "common"]);

    const { authService, apiService, config } = dependencies;

    // initialize keycloak authentication (mheicke)
    useEffect(() => {
        // Set up listener BEFORE starting initialization to avoid race condition
        const unsubscribeInit = authService.onInitializationChanged((isInitialized) => {
            setInitialized(isInitialized);
        });

        // Listen for authentication errors
        const unsubscribeError = authService.onError((error) => {
            setErrorModal({ show: true, error });
        });

        // Start initialization after listener is set up
        authService.init().catch((error) => {
            logger.error("Failed to initialize Keycloak:", error);

            // Show initialization error in modal
            const initError: AuthError = {
                type: "auth",
                title: t("auth:errors.initializationFailed"),
                message: t("auth:errors.initializationFailedMessage"),
                errorCode: "INIT_ERROR",
                details: error?.message || "Unknown initialization error",
                requiresReauth: false,
            };

            setErrorModal({ show: true, error: initError });
        });

        return () => {
            unsubscribeInit();
            unsubscribeError();
        };
    }, [authService, t]);

    useEffect(() => {
        applyAppMetaTags({ version: config.appVersion, environment: config.environment });
    }, [config.appVersion, config.environment]);

    const handleCloseErrorModal = () => {
        setErrorModal({ show: false, error: null });
    };

    const handleReauth = async () => {
        handleCloseErrorModal();
        await authService.reauthenticate();
    };

    if (!initialized) {
        return (
            <div className="App">
                <div className="app-main d-flex justify-content-center align-items-center flex-column">
                    <Spinner animation="border" role="status" className="app-spinner">
                        <span className="visually-hidden">{t("common:loading")}</span>
                    </Spinner>
                    <p className="m-3 p-2">{t("auth:loading.title")}</p>
                </div>
                <Footer version={config.appVersion} />
                <ErrorModal
                    show={errorModal.show}
                    onHide={handleCloseErrorModal}
                    error={errorModal.error}
                    onReauth={handleReauth}
                />
            </div>
        );
    }

    return (
        <div className="App">
            <Navigation authService={authService} apiService={apiService} />
            <main className="app-main">
                <Container>
                    <Row>
                        <Col className="content">
                            <Routes>
                                {/* OAuth2 callback route - accessible during authentication */}
                                <Route path={AUTH_REDIRECT_PATH} element={<OAuth2Callback />} />

                                {/* Login route - accessible when not authenticated */}
                                <Route path="/login" element={<Login authService={authService} />} />

                                {/* Protected routes - only page content is protected */}
                                <Route
                                    path="/"
                                    element={
                                        <ProtectedRoute
                                            element={
                                                <Home
                                                    apiService={apiService}
                                                    maxFileSizeBytes={config.maxFileSizePdfTemplateUploadBytes}
                                                />
                                            }
                                            authService={authService}
                                            apiService={apiService}
                                        />
                                    }
                                />
                                <Route
                                    path="/documents"
                                    element={
                                        <ProtectedRoute
                                            element={<DocumentList apiService={apiService} />}
                                            authService={authService}
                                            apiService={apiService}
                                        />
                                    }
                                />
                                <Route
                                    path="/documents/create"
                                    element={
                                        <ProtectedRoute
                                            element={
                                                <CreateDocuments
                                                    apiService={apiService}
                                                    authService={authService}
                                                    webAppBaseUrl={config.webAppUrl}
                                                />
                                            }
                                            authService={authService}
                                            apiService={apiService}
                                        />
                                    }
                                />
                                <Route
                                    path="/templates"
                                    element={
                                        <ProtectedRoute
                                            element={
                                                <TemplateList
                                                    apiService={apiService}
                                                    maxFileSizeBytes={config.maxFileSizePdfTemplateUploadBytes}
                                                />
                                            }
                                            authService={authService}
                                            apiService={apiService}
                                        />
                                    }
                                />
                                <Route
                                    path="/templates/:id/edit"
                                    element={
                                        <ProtectedRoute
                                            element={<TemplateEditor apiService={apiService} />}
                                            authService={authService}
                                            apiService={apiService}
                                        />
                                    }
                                />

                                {/* Catch-all route for 404 pages - must be last */}
                                <Route path="*" element={<Page404 />} />
                            </Routes>
                        </Col>
                    </Row>
                </Container>
            </main>
            <Footer version={config.appVersion} />

            <ErrorModal
                show={errorModal.show}
                onHide={handleCloseErrorModal}
                error={errorModal.error}
                onReauth={handleReauth}
            />
        </div>
    );
}

// <Route path="*" status={404} element={<Page404 />} />

export default App;
